import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { useFormatter, useLocale, useTranslations } from "use-intl";
import { useCallback, useEffect, useMemo, useState, useTransition } from "react";
import { ChevronLeft, ChevronRight, CloudOff, Flame, X } from "lucide-react";
import { estimateWorkoutMinutes } from "@liftquest/shared/plan-logic";
import { toast } from "sonner";
import { discardSession, previewSwap, type FinishResult } from "./actions";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useWakeLock } from "@/hooks/use-wake-lock";
import { unlockAudio } from "@/lib/feedback";
import * as outbox from "@/lib/outbox";
import { cancelRestAlarm, scheduleRestAlarm } from "@/lib/push";
import type { LoggedSet, TrainingData, TrainingItem } from "./model";
import { cn } from "@/lib/utils";
import { ExerciseStep, type NewSet } from "./exercise-step";
import { FinishStep } from "./finish-step";
import { RestOverlay, type RestState } from "./rest-overlay";
import { ResultScreen } from "./result-screen";
import { WarmupStep } from "./warmup-step";

export type Swap = Pick<TrainingItem, "exercise" | "suggestion" | "last">;

function workingSetsDone(item: TrainingItem, logged: LoggedSet[]) {
  return logged.filter((s) => s.workoutExerciseId === item.workoutExerciseId && !s.isWarmup).length;
}

export function Trainer({ data }: { data: TrainingData }) {
  const t = useTranslations("train");
  const tc = useTranslations("common");
  const navigate = useNavigate();
  const locale = useLocale();
  const format = useFormatter();
  const qc = useQueryClient();
  useWakeLock();
  /** Entered afterwards: no warm-up checklist and no rest timer. */
  const loggedLater = data.session.loggedLater;

  const items = data.items;
  const sessionId = data.session.id;
  // Sets logged on this phone that the server (or the cached training data) doesn't know yet.
  const [initialLogged] = useState<LoggedSet[]>(() => outbox.mergeQueued(data.logged, sessionId));
  const [logged, setLogged] = useState<LoggedSet[]>(initialLogged);
  const pendingSync = outbox.usePendingCount(sessionId);
  const [swaps, setSwaps] = useState<Record<string, Swap>>({});
  const [rest, setRest] = useState<RestState | null>(null);
  const [result, setResult] = useState<FinishResult | null>(null);
  const [quitOpen, setQuitOpen] = useState(false);
  const [discarding, startDiscard] = useTransition();

  const [step, setStep] = useState(() => {
    if (initialLogged.length === 0) return data.warmup.length && !loggedLater ? -1 : 0;
    const firstOpen = items.findIndex((it) => workingSetsDone(it, initialLogged) < it.sets);
    return firstOpen === -1 ? items.length : firstOpen;
  });

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [step]);

  // Phones pause the page in the background, so its timer can't ring: while hidden
  // during a rest, the server sends a push notification when the rest ends.
  useEffect(() => {
    if (!rest) return;
    const alarm = {
      endsAt: rest.endsAt,
      title: t("restOverTitle"),
      body: t("restOverBody", { next: rest.nextLabel }),
      url: `/train/${sessionId}`,
    };
    const onVisibility = () => (document.visibilityState === "hidden" ? scheduleRestAlarm(alarm) : cancelRestAlarm());
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      // Rest skipped or adjusted in the app; when it ran out in the background the push stays.
      if (document.visibilityState === "visible") cancelRestAlarm();
    };
  }, [rest, sessionId, t]);

  // The API refused a queued set for good (e.g. the workout was finished on another device).
  useEffect(
    () =>
      outbox.onRejected((op) => {
        if (op.sessionId !== sessionId || op.kind !== "create") return;
        setLogged((l) => l.filter((s) => s.id !== op.id));
        toast.error(t("setRejected"));
      }),
    [sessionId, t],
  );

  const effective = useCallback(
    (item: TrainingItem): TrainingItem => {
      const swap = swaps[item.workoutExerciseId];
      return swap ? { ...item, ...swap } : item;
    },
    [swaps],
  );

  const totalSets = useMemo(() => items.reduce((n, it) => n + it.sets, 0), [items]);
  const estimatedMinutes = useMemo(
    () =>
      estimateWorkoutMinutes(
        items.map((it) => ({ ...it, type: it.exercise.type })),
        { warmup: data.warmup.length > 0 },
      ),
    [items, data.warmup.length],
  );
  const doneSets = logged.filter((s) => !s.isWarmup).length;

  /** Shows the set right away and queues it for the server (works offline). */
  async function handleLog(item: TrainingItem, set: NewSet) {
    const id = outbox.newSetId();
    const values = {
      workoutExerciseId: item.workoutExerciseId,
      exerciseId: item.exercise.id,
      setIndex: set.setIndex,
      isWarmup: set.isWarmup,
      weight: set.weight,
      reps: set.reps,
      durationSec: set.durationSec,
      distanceKm: set.distanceKm,
    };
    const optimistic: LoggedSet = { id, rir: null, ...values };
    setLogged((l) => [...l, optimistic]);
    outbox.logSet(sessionId, id, values);

    const doneAfter = workingSetsDone(item, [...logged, optimistic]);
    const exerciseDone = !set.isWarmup && doneAfter >= item.sets;
    const isLastExercise = step >= items.length - 1;
    const restSec = set.isWarmup ? 45 : item.restSec;
    if (loggedLater) {
      if (exerciseDone && !isLastExercise) setStep((s) => s + 1);
    } else if (restSec > 0 && !(exerciseDone && isLastExercise) && item.exercise.type !== "cardio") {
      const next = exerciseDone ? items[step + 1] : null;
      const moreWarmups = set.isWarmup && set.setIndex + 1 < item.warmupSets;
      setRest({
        endsAt: Date.now() + restSec * 1000,
        total: restSec,
        nextLabel: next
          ? effective(next).exercise.name
          : moreWarmups
            ? `${t("warmupSet")} ${set.setIndex + 2}`
            : t("set", { n: doneAfter + 1 }),
        askRirFor: !set.isWarmup && item.exercise.type === "reps" ? id : null,
      });
    }
  }

  function handleUndo(setId: string) {
    setLogged((l) => l.filter((s) => s.id !== setId));
    outbox.undoSet(sessionId, setId);
  }

  function handleRir(setId: string, rir: number) {
    setLogged((l) => l.map((s) => (s.id === setId ? { ...s, rir } : s)));
    outbox.setRir(sessionId, setId, rir);
  }

  async function handleSwap(item: TrainingItem, exerciseId: string | null) {
    const target = exerciseId ?? item.original.id;
    try {
      const preview = await previewSwap(data.session.id, item.workoutExerciseId, target, locale);
      setSwaps((s) => ({ ...s, [item.workoutExerciseId]: preview }));
    } catch {
      toast.error(tc("error"));
    }
  }

  if (result) {
    return (
      <ResultScreen
        result={result}
        onDone={() => {
          void qc.invalidateQueries();
          navigate("/dashboard", { replace: true });
        }}
      />
    );
  }

  const current = step >= 0 && step < items.length ? effective(items[step]) : null;

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-md flex-col" onPointerDown={unlockAudio}>
      {/* Top bar */}
      <header className="sticky top-0 z-30 border-b bg-background/90 pt-safe backdrop-blur-xl">
        <div className="flex items-center gap-2 px-3 py-2">
          <Button variant="ghost" size="icon-lg" className="rounded-full" onClick={() => setQuitOpen(true)} aria-label={t("quit")}>
            <X className="size-6" />
          </Button>
          <div className="min-w-0 flex-1 text-center">
            <p className="truncate text-sm font-semibold">{data.session.name}</p>
            {loggedLater && (
              <p className="text-xs font-medium text-primary">
                {t("loggedLater", { date: format.dateTime(new Date(data.session.startedAt), { dateStyle: "medium" }) })}
              </p>
            )}
            <p className="text-xs tabular-nums text-muted-foreground">
              {step >= 0 && step < items.length
                ? t("exerciseOf", { current: step + 1, total: items.length })
                : t("setsLogged", { done: doneSets, total: totalSets })}
            </p>
          </div>
          <div className="flex w-11 items-center justify-end gap-0.5 text-xs font-semibold tabular-nums text-xp">
            <Flame className="size-4" />
            {doneSets}
          </div>
        </div>
        {pendingSync > 0 && (
          <p role="status" className="flex items-center justify-center gap-1.5 pb-1.5 text-xs font-medium text-muted-foreground">
            <CloudOff className="size-3.5" /> {t("pendingSync", { count: pendingSync })}
          </p>
        )}
        <div className="flex gap-1 px-3 pb-2">
          {items.map((it, i) => {
            const done = workingSetsDone(it, logged);
            return (
              <button
                key={it.workoutExerciseId}
                type="button"
                aria-label={effective(it).exercise.name}
                onClick={() => setStep(i)}
                className={cn(
                  "h-1.5 flex-1 overflow-hidden rounded-full bg-foreground/10",
                  i === step && "ring-2 ring-primary/40 ring-offset-1 ring-offset-background",
                )}
              >
                <span
                  className="block h-full rounded-full bg-primary transition-[width]"
                  style={{ width: `${Math.min(100, (done / it.sets) * 100)}%` }}
                />
              </button>
            );
          })}
        </div>
      </header>

      <main className="flex-1 px-4 pb-32">
        {step === -1 && <WarmupStep steps={data.warmup} onDone={() => setStep(0)} />}
        {current && (
          <ExerciseStep
            key={`${current.workoutExerciseId}-${current.exercise.id}`}
            item={current}
            isFirst={step === 0}
            phase={data.phase}
            week={data.session.week}
            beginner={data.settings.beginnerMode}
            logged={logged.filter((s) => s.workoutExerciseId === current.workoutExerciseId)}
            library={data.library}
            onLog={(s) => handleLog(current, s)}
            onUndo={handleUndo}
            onSwap={(id) => handleSwap(items[step], id)}
            swapped={!!swaps[current.workoutExerciseId] || current.exercise.id !== current.original.id}
          />
        )}
        {step === items.length && items.length > 0 && (
          <Button variant="ghost" className="mt-3 -ml-2" onClick={() => setStep(items.length - 1)}>
            <ChevronLeft /> {effective(items[items.length - 1]).exercise.name}
          </Button>
        )}
        {step === items.length && (
          <FinishStep
            sessionId={data.session.id}
            startedAt={data.session.startedAt}
            logged={logged}
            isCardio={data.session.kind === "cardio"}
            loggedLater={loggedLater ? { defaultMinutes: estimatedMinutes || 60 } : null}
            onFinished={setResult}
          />
        )}
      </main>

      {/* Bottom navigation between exercises */}
      {step >= 0 && step < items.length && (
        <nav className="fixed inset-x-0 bottom-0 z-30 border-t bg-background/90 pb-safe backdrop-blur-xl">
          <div className="mx-auto flex max-w-md items-center gap-2 px-4 pt-2">
            <Button
              variant="outline"
              size="icon-lg"
              className="size-12 rounded-2xl"
              disabled={step === 0}
              onClick={() => setStep((s) => s - 1)}
              aria-label={t("upNext")}
            >
              <ChevronLeft />
            </Button>
            {(() => {
              const allDone = current ? workingSetsDone(current, logged) >= current.sets : false;
              const last = step === items.length - 1;
              return (
                <Button
                  size="lg"
                  variant={allDone ? "default" : "secondary"}
                  className="h-12 flex-1 rounded-2xl text-base"
                  onClick={() => setStep((s) => s + 1)}
                >
                  {allDone ? (last ? t("toFinish") : t("nextExercise")) : t("skipExercise")}
                  <ChevronRight />
                </Button>
              );
            })()}
          </div>
        </nav>
      )}

      {rest && (
        <RestOverlay
          state={rest}
          sound={data.settings.sound}
          vibration={data.settings.vibration}
          beginner={data.settings.beginnerMode}
          currentRir={rest.askRirFor ? (logged.find((s) => s.id === rest.askRirFor)?.rir ?? null) : null}
          onRir={(rir) => rest.askRirFor && handleRir(rest.askRirFor, rir)}
          onChange={setRest}
          onClose={() => {
            setRest(null);
            if (current && workingSetsDone(current, logged) >= current.sets && step < items.length) {
              setStep((s) => s + 1);
            }
          }}
        />
      )}

      <AlertDialog open={quitOpen} onOpenChange={setQuitOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("quitTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("quitText")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => navigate("/dashboard")}>{t("later")}</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={discarding}
              onClick={() =>
                startDiscard(async () => {
                  await discardSession(sessionId);
                  outbox.clearSession(sessionId);
                  await qc.invalidateQueries();
                  navigate("/dashboard", { replace: true });
                })
              }
            >
              {t("discard")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
